import { Filter, Pagination, Search, Sort } from "@/components/Table/Table";
import { YearStatus } from "@/components/YearSelector/YearSelector";
import { filterSearchToGetFilter, GetData, sortToParam } from "@/utilities/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import useSmartQueryClient from "./useSmartQueryClient";

export type OrderType = "purchase" | "sales";

export interface IOrder {
  id: number;
  order_date: string;
  order_id: string;
  bom_item_id: number;
  quantity: number;
  transport_category: string;
  product_id: number;
  product_external_id: string;
  product_name: string | null;
  departure_facility_id: number;
  departure_facility_name: string;
  departure_facility_address: string | null;
  arrival_facility_id: number | null;
  arrival_facility_name: string | null;
  arrival_facility_address: string | null;
  arrival_name: string | null;
  arrival_address: string | null;
  status: "complete";
}

export function useOrders({
  orderType,
  year,
  pagination,
  filter,
  search,
  sort,
  enabled = true,
}: {
  orderType: OrderType;
  year?: number;
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  enabled?: boolean;
}) {
  const extraParams = year ? { year } : {};

  return useQuery({
    queryKey: ["orders", "list", orderType, year, pagination, filter, search, sort],
    keepPreviousData: true,
    enabled: enabled,
    queryFn: () =>
      axios
        .get<GetData<IOrder>>("/orders", {
          params: {
            ...extraParams,
            order_type: orderType,
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
          },
        })
        .then((result) => result.data),
  });
}

export function useOrderStatusPerYear({ orderType }: { orderType: OrderType }) {
  return useQuery({
    queryKey: ["orders", "years", orderType],
    keepPreviousData: true,
    queryFn: () =>
      axios
        .get<YearStatus[]>("/orders/years", {
          params: {
            order_type: orderType,
          },
        })
        .then((result) => result.data),
  });
}

export function useDeleteOrders() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ orderType, year }: { orderType: OrderType; year?: number }) =>
      axios.delete<{ count: number }>("/orders", {
        params: {
          ...(year ? { year } : {}),
          order_type: orderType,
        },
      }),
    onSuccess: (_, { orderType, year }) => {
      smartQueryClient.invalidateQueries(["orders", "list", orderType]);
      smartQueryClient.invalidateQueries(["orders", "years", orderType]);
    },
  });
}

export const exportOrders = async ({
  year,
  type,
}: {
  year?: number;
  type: "sales" | "purchase";
}): Promise<string> => {
  const { data } = await axios.get<{ url: string }>(`/orders/export`, {
    params: { order_type: type, ...(year ? { year } : {}) },
  });
  return data.url;
};
